import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchSelect } from './FieldComponents';
import { caseDbApiEndpoint } from '../utils/apiOrigin';

const positiveId = (value) => (typeof value === 'string' || typeof value === 'number')
  && /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
const isDeleted = (value) => value != null && Number(value) !== 0;
const caseOption = (record) => ({
  value: String(record.case_id ?? record.id),
  label: String(record.case_name || `Case ${record.case_id ?? record.id}`),
  phase: record.phase_name || '',
});

const renderCaseOption = (option) => <span className="mini-case-meta">
  <span className="mini-case-name"><span>{option.label}</span><span className="subtext">({option.value})</span></span>
  {option.phase && <span className="mini-case-info subtext">{option.phase}</span>}
</span>;

const PublicFormCaseSelect = ({ value, selectedCase, onChange, onResolved, caseTypeId, inputRef, disabled = false, error, casesEndpoint }) => {
  const [searchError, setSearchError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const searchRef = useRef(null);
  const unavailable = disabled || !positiveId(caseTypeId);
  const hasResolvedSelection = selectedCase?.value === String(value);
  const options = useMemo(() => !positiveId(value) ? [] : [
    hasResolvedSelection ? selectedCase : { value: String(value), label: `Case ${value}` },
  ], [hasResolvedSelection, selectedCase, value]);

  useEffect(() => {
    setSearchError('');
    return () => searchRef.current?.abort();
  }, [caseTypeId, casesEndpoint, unavailable]);

  useEffect(() => {
    setSelectionError('');
    if (unavailable || !positiveId(value) || hasResolvedSelection) return undefined;
    const controller = new AbortController();
    const hydrate = async () => {
      try {
        const url = new URL(casesEndpoint || caseDbApiEndpoint('cases.php'), window.location.href);
        url.searchParams.set('id', String(value));
        const response = await fetch(url.toString(), { credentials: 'include', cache: 'no-store', signal: controller.signal });
        const payload = await response.json();
        if (controller.signal.aborted) return;
        // The detail route is authenticated but intentionally includes archived
        // and deleted records. Match the existing form target eligibility here.
        if (!response.ok || payload.success === false || !payload.case || !payload.lead
          || Number(payload.case.case_id ?? payload.case.id) !== Number(value)
          || isDeleted(payload.case.deleted) || isDeleted(payload.lead.deleted)
          || Number(payload.lead.case_type_id) !== Number(caseTypeId)) {
          throw new Error('The selected case is unavailable. Search for an accessible case matching this form’s case type.');
        }
        onResolved?.(caseOption(payload.case));
      } catch (requestError) {
        if (!controller.signal.aborted) setSelectionError('The selected case could not be loaded. Search for a case or try again.');
      }
    };
    hydrate();
    return () => controller.abort();
  }, [caseTypeId, casesEndpoint, hasResolvedSelection, onResolved, unavailable, value]);

  const loadCaseOptions = useCallback(async (query = '') => {
    if (unavailable) return [];
    searchRef.current?.abort();
    const controller = new AbortController();
    searchRef.current = controller;
    setSearchError('');
    try {
      const url = new URL(casesEndpoint || caseDbApiEndpoint('cases.php'), window.location.href);
      Object.entries({ page: 1, per_page: 100, case_types: caseTypeId, search: query.trim(),
        sort_by: 'case_name', sort_order: 'ASC', show_archived: 'true' }).forEach(([key, item]) => {
        if (item !== '') url.searchParams.set(key, String(item));
      });
      const response = await fetch(url.toString(), { credentials: 'include', cache: 'no-store', signal: controller.signal });
      const payload = await response.json();
      if (controller.signal.aborted) return [];
      if (!response.ok || payload.success !== true || !Array.isArray(payload.cases)) throw new Error('Cases could not be loaded.');
      // Both lists are filtered by the server; never select the first result
      // automatically. Individual links retain support for archived cases.
      const rows = [...(Array.isArray(payload.pinned_cases) ? payload.pinned_cases : []), ...payload.cases];
      const unique = new Map(rows.filter((record) => record && positiveId(record.case_id))
        .map((record) => [String(record.case_id), caseOption(record)]));
      return [...unique.values()];
    } catch (requestError) {
      if (!controller.signal.aborted) setSearchError('Cases could not be loaded. Search again to retry.');
      return [];
    }
  }, [caseTypeId, casesEndpoint, unavailable]);

  const message = error || searchError || selectionError;
  return <div className={`form-group nm full public-form-target-case-search${message ? ' has-error' : ''}`}>
    <label className="subtext" htmlFor="public-form-target-id">Case for this link <span className="required" aria-hidden="true">*</span></label>
    <SearchSelect
      key={String(caseTypeId)}
      inputId="public-form-target-id"
      inputRef={inputRef}
      ariaLabel="Case for this link"
      ariaRequired
      ariaInvalid={Boolean(message)}
      ariaDescribedBy={`public-form-target-help${message ? ' public-form-target-error' : ''}`}
      value={value}
      options={options}
      placeholder="Search by case name, client, or ID"
      loadOptions={loadCaseOptions}
      loadOptionsMinChars={2}
      returnOptionObject
      renderOption={renderCaseOption}
      disabled={unavailable}
      onChange={(option) => {
        if (option !== null && !positiveId(option?.value)) return;
        setSelectionError('');
        setSearchError('');
        onChange(option);
      }}
    />
    <small id="public-form-target-help">Choose a case you can access that matches this form’s case type. Publishing and previewing do not require a case or send anything.</small>
    {hasResolvedSelection && <small>Selected: {selectedCase.label} ({selectedCase.value})</small>}
    {message && <small id="public-form-target-error" role="alert">{message}</small>}
  </div>;
};

export default PublicFormCaseSelect;
